//
// Used in the redux and components to organize action related data and provide helper methods.
//

import Action from './Action';
import FrontEndStore from './FrontEndStore';

export const ACTION_TYPES = [
  'custom',
  'email',
  'linkedin',
  'facebook',
  'other',
  'petition',
  'phone',
  'twitter',
];
export const FIXED_ACTION_TYPES = ACTION_TYPES.filter(t => t !== 'custom');

class ActionsStore extends FrontEndStore {
  constructor(actionData) {
    super();
    this.storeType = ActionsStore;
    this.loading = !actionData; // If no data object supplied, assume unloaded.
    this.store = ((actionData && actionData.store) || actionData || [])
      // In case cached code doesn't include new types ↓
      .filter(datum => ACTION_TYPES.includes(datum.actionType))
      .map(datum => new Action(datum, datum.actionType));
  }

  // FILTERS / Arrays

  all() {
    return this.store;
  }

  excluding(otherActionStore) {
    const actionData = this.store.filter(
      action => !otherActionStore.find(action.actionType, action.id),
    );
    return new ActionsStore(actionData);
  }

  unsnoozed() {
    return new ActionsStore(this.all().filter(action => !action.isSnoozed()));
  }

  filter(options) {
    let actionArr = this.all();
    if (options.enabledState !== 'all') {
      actionArr = actionArr.filter(act => act[`is${capitalize(options.enabledState)}`]());
    }
    if (options.region !== 'all') {
      actionArr = actionArr.filter(act =>
        act.regions.find(region => `${region.id}` === `${options.region}`),
      );
    }
    if (options.actionType !== 'all') {
      actionArr = actionArr.filter(
        act =>
          act.actionType === options.actionType ||
          (act.actionTypeTemplate &&
            act.actionTypeTemplate.name.toLowerCase() === options.actionType.toLowerCase()),
      );
    }
    if (options.search !== '') {
      actionArr = actionArr.filter(act => act.contentMatches(options.search));
    }
    return new ActionsStore(
      actionArr.sort((a, b) => (a.frozenUpdatedAt > b.frozenUpdatedAt ? -1 : 1)),
    );
  }

  removeActionsByType(actionTypeSet) {
    this.store = this.store.filter(action => !actionTypeSet.has(action.actionType));
  }

  find(actionType, actionId) {
    return (
      this.store.find(
        nextAction => nextAction.id === Number(actionId) && nextAction.actionType === actionType,
      ) || false
    );
  }

  findAll(typesAndIds) {
    const actions = typesAndIds
      .map(([actionType, actionId]) => this.find(actionType, actionId))
      .filter(a => !!a);
    return new ActionsStore(actions);
  }

  presentActionTypes() {
    return ACTION_TYPES.filter(type => this.store.find(a => a.actionType === type));
  }

  orderedByRecency() {
    return this.all().sort((a, b) => (a.updatedAt > b.updatedAt ? -1 : 1));
  }

  orderedByCreation() {
    return this.all().sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1));
  }

  // Most recently launched first, then soonest ending (open-ended last), then id and action type
  // so ties are deterministic. Each action type has its own id sequence, so id alone can tie.
  // Unlike updatedAt, launch time isn't moved by admin edits or completion-counter writes.
  // An action launches at its startTime, or when it was created if that is later: a duplicated
  // action keeps its original's startTime but only goes live once it exists.
  orderedByLaunch() {
    const time = date => (date ? new Date(date).getTime() || 0 : 0);
    const launch = action => Math.max(time(action.startTime), time(action.createdAt));
    const end = action => (action.endTime ? new Date(action.endTime).getTime() : Infinity);
    return this.all().sort((a, b) => {
      if (launch(a) !== launch(b)) return launch(b) - launch(a);
      // Compared rather than subtracted: two open-ended actions would give Infinity - Infinity.
      if (end(a) !== end(b)) return end(a) < end(b) ? -1 : 1;
      if (a.id !== b.id) return b.id - a.id;
      return a.actionType.localeCompare(b.actionType);
    });
  }

  // Leaves order alone unless prioirity supercedes. Missing priority sorts as the lowest level, 1.
  orderedByPriority() {
    return this.all().sort((a, b) => {
      const aPriority = a.priority ?? 1;
      const bPriority = b.priority ?? 1;
      if (aPriority === bPriority) return 0;
      return aPriority > bPriority ? -1 : 1;
    });
  }

  // Mutations

  addStore(otherActionsStore) {
    otherActionsStore.store.forEach(action => this.addAction(action));
  }

  addAction(action) {
    if (this.find(action.actionType, action.id)) return;
    this.store.push(action);
  }

  prependAction(action) {
    if (this.find(action.actionType, action.id)) return;
    this.store.unshift(action);
  }

  markCompleted(actionType, actionId) {
    const action = this.find(actionType, actionId);
    // Action may not exist in this store
    if (action) {
      action.completed = true;
    }
  }

  markUncompleted(actionType, actionId) {
    const action = this.find(actionType, actionId);
    action.completed = false;
  }

  remove(actionType, actionId) {
    this.store = this.store.filter(
      action => !(action.id === actionId && action.actionType === actionType),
    );
  }

  updateAction(actionData) {
    const action = this.find(actionData.actionType, actionData.id);
    if (!action) return;
    action.update(actionData);
  }

  // Data

  totalActionsCount() {
    return this.all().length;
  }

  isEmpty() {
    return this.totalActionsCount() === 0;
  }

  areAllCompleted() {
    return !this.isLoading() && this.all().every(action => action.completed);
  }

  isLoading() {
    return this.loading;
  }

  supplementCampaigns(campaignStore) {
    this.store.map(a => a.supplementCampaign(campaignStore));
  }
}

function capitalize(s) {
  return s[0].toUpperCase() + s.slice(1);
}

// Adds nextEmail, nextPhone .. functions.
for (const actionType of ACTION_TYPES) {
  // eslint-disable-next-line func-names
  ActionsStore.prototype[`next${capitalize(actionType)}Action`] = function() {
    return this.store.find(action => action.actionType === actionType);
  };
  // eslint-disable-next-line func-names
  ActionsStore.prototype[`${actionType}Actions`] = function() {
    return this.store.filter(action => action.actionType === actionType);
  };
}

export default ActionsStore;
